import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Layout, SectionTitle } from "@/components/Layout";
import { DeviceCard, AppCard, TutorialCard, HelpBanner } from "@/components/cards";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Search, X, ArrowRight, Megaphone, Star, History, ShieldCheck, Mic } from "lucide-react";
import { getRecents, getFavorites } from "@/lib/local";
import { VoiceButton } from "@/components/VoiceButton";
import { AnnouncementPopup } from "@/components/AnnouncementPopup";
import { Seo } from "@/components/Seo";
import { SearchResults, StatusMini } from "@/components/HomeParts";

const POPULAR = ["Samsung", "LG", "Android", "Fire TV", "TV Box", "iPhone"];

export default function Home() {
  const nav = useNavigate();
  const { openWhatsApp } = useSettings();
  const [devices, setDevices] = useState([]);
  const [apps, setApps] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [anns, setAnns] = useState([]);
  const [status, setStatus] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [recents] = useState(getRecents());
  const [favs] = useState(getFavorites());
  const debounce = useRef(null);

  useEffect(() => {
    api.get("/devices").then((r) => setDevices(r.data));
    api.get("/apps").then((r) => setApps(r.data));
    api.get("/tutorials").then((r) => setTutorials(r.data));
    api.get("/announcements").then((r) => setAnns(r.data));
    api.get("/status").then((r) => setStatus(r.data));
  }, []);

  useEffect(() => {
    clearTimeout(debounce.current);
    if (!q.trim()) { setResults(null); return; }
    debounce.current = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data));
    }, 250);
  }, [q]);

  const allOnline = status.length > 0 && status.every((s) => s.status === "online");

  return (
    <Layout>
      <Seo path="/" />
      <AnnouncementPopup />
      {/* HERO */}
      <section className="ci-bg-grid relative z-[60]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-16 pb-14 sm:pt-24 sm:pb-20">
          <div className="max-w-3xl ci-reveal">
            <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-cyan-300 mb-6">
              <ShieldCheck className="w-4 h-4" /> Suporte profissional 24/7
              {allOnline && <span className="text-emerald-400">· Sistemas Online</span>}
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Central de Suporte <br className="hidden sm:block" />
              <span className="ci-gold-text">Club</span> <span className="ci-cyan-text">Infinity</span>
            </h1>
            <p className="text-gray-300 text-base sm:text-lg mt-5 max-w-2xl leading-relaxed">
              Encontre tutoriais rápidos e fáceis para configurar seu dispositivo. Passo a passo, aplicativos oficiais e ajuda direta pelo WhatsApp.
            </p>

            {/* SEARCH */}
            <div className="mt-8 relative z-[60]">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="O que você deseja configurar? (ex: Samsung, Android...)"
                  className="w-full bg-[#131C2E] border border-white/10 focus:border-cyan-400/60 rounded-2xl pl-12 pr-24 py-4 text-base outline-none transition-colors shadow-lg"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {q && (
                    <button onClick={() => setQ("")} data-testid="hero-search-clear" className="p-1.5 rounded-lg hover:bg-white/10">
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                  <VoiceButton onResult={(t) => setQ(t)} />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5"><Mic className="w-3.5 h-3.5" /> Toque no microfone e diga o nome do seu aparelho (ex: "Samsung")</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {POPULAR.map((p) => (
                  <button key={p} onClick={() => setQ(p)} data-testid={`popular-tag-${p.toLowerCase().replace(/\s+/g,'-')}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 transition">{p}</button>
                ))}
              </div>

              {results && <SearchResults results={results} onClose={() => setQ("")} />}
            </div>

            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={() => nav("/dispositivos")} data-testid="hero-start-btn"
                className="ci-btn-gold inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold hover:brightness-105 transition">
                COMEÇAR AGORA <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => openWhatsApp("hero")} data-testid="hero-whatsapp-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold border border-white/15 hover:bg-white/5 transition">
                Falar com atendente
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ANNOUNCEMENTS */}
      {anns.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 -mt-6 relative">
          <div className="grid gap-4 sm:grid-cols-2">
            {anns.map((a) => (
              <div key={a.id} data-testid={`announcement-${a.id}`} className="ci-card p-5 flex items-start gap-3 border-amber-400/20">
                <Megaphone className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">{a.title}</div>
                  <p className="text-sm text-gray-400 mt-0.5">{a.message}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* RECENTS / FAVORITES */}
      {(recents.length > 0 || favs.length > 0) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-14 grid gap-8 lg:grid-cols-2">
          {recents.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4"><History className="w-5 h-5 text-cyan-300" /><h3 className="font-semibold">Vistos recentemente</h3></div>
              <div className="space-y-2">{recents.slice(0,3).map((t) => <TutorialCard key={t.slug} tut={t} />)}</div>
            </div>
          )}
          {favs.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4"><Star className="w-5 h-5 text-amber-300" /><h3 className="font-semibold">Meus favoritos</h3></div>
              <div className="space-y-2">{favs.slice(0,3).map((t) => <TutorialCard key={t.slug} tut={t} />)}</div>
            </div>
          )}
        </section>
      )}

      {/* DEVICES */}
      <section id="dispositivos" className="max-w-7xl mx-auto px-4 sm:px-8 mt-20">
        <SectionTitle label="Escolha seu dispositivo" title="Como você quer assistir?"
          subtitle="Selecione seu dispositivo e veja os tutoriais disponíveis." />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {devices.map((d) => <DeviceCard key={d.id} device={d} />)}
        </div>
        <div className="mt-8">
          <HelpBanner onWhatsApp={() => openWhatsApp("device-not-found")} />
        </div>
      </section>

      {/* APPS PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-20">
        <SectionTitle label="Aplicativos recomendados" title="Apps oficiais Club Infinity"
          subtitle="Aplicativos organizados e compatíveis com seus dispositivos." />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {apps.slice(0, 8).map((a) => <AppCard key={a.id} app={a} />)}
        </div>
        <button onClick={() => nav("/aplicativos")} data-testid="see-all-apps-btn"
          className="mt-6 inline-flex items-center gap-2 text-cyan-300 font-semibold hover:gap-3 transition-all">
          Ver todos os aplicativos <ArrowRight className="w-4 h-4" />
        </button>
      </section>

      {/* TUTORIALS PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-20">
        <SectionTitle label="Tutoriais" title="Passo a passo simplificado"
          subtitle="Guias claros para configurar em poucos minutos." />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {tutorials.slice(0, 6).map((t) => <TutorialCard key={t.id} tut={t} />)}
        </div>
      </section>

      {/* STATUS PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-20">
        <SectionTitle label="Status dos serviços" title="Tudo sob controle"
          subtitle="Acompanhe em tempo real a saúde dos nossos serviços." />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {status.map((s) => <StatusMini key={s.id} s={s} />)}
        </div>
      </section>

      <div className="h-10" />
    </Layout>
  );
}
